<script setup>
import Panel from '../common/Panel.vue';
import Slider from '@maison/components/organisms/Slider.vue';
import MovementGridSlider from './MovementGridSlider.vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import PhxPicture from '@maison/components/common/PhxPicture.vue';

const props = defineProps({
  movement: {
    type: Object,
    default: () => ({}),
  },
  watches: {
    type: Array,
    default: () => [],
  },
});

const { t } = useI18n();

const displayedPropertyIds = [
  'standardizedEnergy',
  'standardizedDiameter',
  'standardizedThickness',
  'standardizedNumberOfPieces',
  'standardizedNumberOfChatons',
  'standardizedNumberOfRubis',
  'standardizedPowerReserve',
  'balanceFrequency',
  'functions_*_standardizedType',
];

const movementSliderDesktop = ref(null);
const movementSliderMobile = ref(null);

const hasMedias = computed(() => props.movement?.medias?.length > 0);
const hasWatches = computed(() => props.watches?.length > 0);
const panelTitle = computed(() => t('MovementGrid.panel.title', { reference: props.movement?.panelTitle ?? '' }));
const emptySlidesCount = computed(() => Math.max(0, variationsSliderOptions.slidesPerView - props.watches.length));
const movementProperties = computed(() =>
  (props.movement?.properties || []).filter((property) => property.id && displayedPropertyIds.includes(property.id)),
);

const variationsSliderOptions = {
  freeMode: true,
  slidesPerView: 2,
  mousewheel: {
    forceToAxis: true,
  },
  spaceBetween: 40,
  breakpoints: {
    768: {
      slidesPerView: 3,
      spaceBetween: 10,
    },
  },
};

const panelOptions = computed(() => ({
  onHidden: () => {
    movementSliderDesktop.value?.resetPosition();
    movementSliderMobile.value?.resetPosition();
  },
}));

const pushReferenceTracking = (watchReference) => {
  if (!window.tracking || !watchReference) {
    return;
  }
  window.tracking.pushEvent(
    window.tracking.getMovementReferenceSelectedTracking(props.movement.modelReference, watchReference),
  );
};
</script>

<template>
  <panel
    body-data-cy="movement-details-panel-body"
    tight
    reverse
    :two-columns="hasMedias"
    :title="panelTitle"
    :classes="['movement-details-panel']"
    :options="panelOptions"
  >
    <template #start>
      <movement-grid-slider
        v-if="hasMedias"
        id="movement-slider-desktop"
        ref="movementSliderDesktop"
        :medias="movement.medias"
      />
    </template>
    <template v-if="movement" #content>
      <div class="movement-details-panel__block">
        <movement-grid-slider
          v-if="hasMedias"
          id="movement-slider-mobile"
          ref="movementSliderMobile"
          :medias="movement.medias"
        />
        <div class="movement-details-panel__body">
          {{ movement.description }}
        </div>
      </div>

      <div v-if="movement.properties" class="movement-details-panel__block">
        <ul class="movement-details-panel__specs list list--stacked">
          <li v-if="movement.movementReleaseYear" class="movement-details-panel__specs-item">
            <h5 class="movement-details-panel__specs-title">{{ t('MovementGrid.panel.ReleaseYear') }}</h5>
            <p class="movement-details-panel__specs-spec">{{ movement.movementReleaseYear }}</p>
          </li>
          <li v-for="property of movementProperties" :key="property.id" class="movement-details-panel__specs-item">
            <h5 class="movement-details-panel__specs-title">{{ property.label }}</h5>
            <p class="movement-details-panel__specs-spec">{{ property.value }}</p>
          </li>
        </ul>
      </div>

      <div v-if="hasWatches" class="movement-details-panel__block">
        <div class="mb-base text-bold">{{ t('MovementGrid.panel.watches.title') }}</div>
        <slider :slider-options="variationsSliderOptions" class="slider--with-scrollbar">
          <template #default>
            <div
              v-for="(watch, index) of watches"
              :key="watch.internalReference"
              :aria-labelledby="`pagination-${index + 1}`"
              class="slider__item"
              role="tabpanel"
            >
              <div class="product-card product-card--no-action product-card--text-on-hover">
                <div class="picture picture--full product-card__picture">
                  <PhxPicture lazy v-bind="watch.image" />
                </div>
                <div class="product-card__description">
                  <a
                    :href="watch.url"
                    class="product-card__link expand-target"
                    @click="() => pushReferenceTracking(watch.internalReference)"
                  >
                    <div class="product-card__name">{{ watch.title }}</div>
                    <div class="product-card__collection">{{ watch.subtitle }}</div>
                    <div class="product-card__ref">{{ watch.internalReference }}</div>
                  </a>
                </div>
              </div>
            </div>

            <div v-for="i in emptySlidesCount" :key="`empty-slide-${i}`" class="slider__item empty-slide"></div>
          </template>
        </slider>
      </div>
      <div v-else class="movement-details-panel__block">
        <div class="mb-base text-bold">{{ t('MovementGrid.panel.watches.title') }}</div>
        <p>{{ t('MovementGrid.panel.watches.hasNoWatch') }}</p>
      </div>
    </template>
  </panel>
</template>
